import { ACCESS, type ApprovalStep, type Claim, type User } from './types.js';

/**
 * A read-only view of the employee directory. The prototype reached into a
 * module-level USERS array; the real system passes in whatever the caller has
 * loaded (a DB query on the server, a cached page on the client) so the routing
 * rules stay pure and testable.
 */
export interface Directory {
  byId(id: string): User | undefined;
  all(): User[];
}

export function makeDirectory(users: User[]): Directory {
  const index = new Map(users.map((u) => [u.id, u]));
  return {
    byId: (id) => index.get(id),
    all: () => users,
  };
}

/**
 * Executives are identified by configured Employment Level or System Access —
 * never by name. A rank change takes effect on the employee's next claim.
 */
export function isExecutiveUser(user: User): boolean {
  return (
    user.employmentLevel === 'Executive' || user.systemAccess === ACCESS.EXECUTIVE_APPROVER
  );
}

/**
 * Walk upward past any deactivated manager to the first active one. The guard
 * bounds the walk so a cyclic `reportsTo` (which the org-chart editor rejects,
 * but which could exist in imported data) cannot hang the request.
 */
export function activeManagerAbove(directory: Directory, userId: string): User | null {
  let cur = directory.byId(userId);
  let guard = 0;
  while (cur && cur.reportsTo && guard < 20) {
    const mgr = directory.byId(cur.reportsTo);
    if (mgr && mgr.active) return mgr;
    cur = mgr;
    guard++;
  }
  return null;
}

/**
 * Build the business-approval chain for a claim, derived purely from the
 * organization chart. Never hard-coded to a named person.
 *
 * - Executives skip Manager/Lead approval and go straight to Finance Review.
 * - A rank-and-file employee under a Manager gets Checked By manager, then
 *   Approved By that manager's own manager (the lead), when one exists.
 * - Anyone reporting directly to a Lead/Executive gets a single approval step.
 * - Someone with no active manager at all falls back to an Executive Approver.
 */
export function buildApprovalChain(directory: Directory, user: User): ApprovalStep[] {
  if (isExecutiveUser(user)) return [];

  const manager = activeManagerAbove(directory, user.id);
  if (!manager) {
    const exec = directory
      .all()
      .find(
        (u) => u.systemAccess === ACCESS.EXECUTIVE_APPROVER && u.active && u.id !== user.id,
      );
    return exec ? [step('Approved By', exec.id)] : [];
  }

  if (manager.systemAccess === ACCESS.MANAGER_APPROVER) {
    const lead = activeManagerAbove(directory, manager.id);
    const chain = [step('Checked By', manager.id)];
    if (lead) chain.push(step('Approved By', lead.id));
    return chain;
  }

  return [step('Approved By', manager.id)];
}

function step(role: ApprovalStep['role'], userId: string): ApprovalStep {
  return { role, userId, decision: null, reason: '', ts: null };
}

/** The first step still awaiting a decision, or undefined when the chain is done. */
export function currentApprovalStep(claim: Claim): ApprovalStep | undefined {
  return claim.approvalChain.find((s) => s.decision === null);
}

/**
 * Detect whether pointing `empId` at `newManagerId` would create a reporting
 * cycle. Used by the org chart before any re-parent is written.
 */
export function wouldCreateCycle(
  directory: Directory,
  empId: string,
  newManagerId: string | null,
): boolean {
  if (!newManagerId) return false;
  if (empId === newManagerId) return true;
  let cur = directory.byId(newManagerId);
  let guard = 0;
  while (cur && guard < 100) {
    if (cur.id === empId) return true;
    cur = cur.reportsTo ? directory.byId(cur.reportsTo) : undefined;
    guard++;
  }
  return false;
}

export function directReports(directory: Directory, userId: string): User[] {
  return directory.all().filter((u) => u.reportsTo === userId && u.active);
}
